import { api } from '@goaltree/backend/convex/_generated/api'
import { coachDisplayName, coachNameOverride } from '@goaltree/backend/convex/lib/coach'
import type { PlanTerm } from '@goaltree/backend/convex/lib/planTerm'
import {
  FREE_GOAL_LIMIT,
  FREE_HABIT_LIMIT,
  type UsageFeature,
} from '@goaltree/backend/convex/lib/usage'
import { Dialog, DialogContent } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  ChevronDown,
  Flame,
  Gauge,
  Lock,
  Mic,
  Palette,
  PencilLine,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  Video,
  X,
} from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CoachAvatar } from '@/components/coach-avatar'
import { useIsPro } from '@/hooks/use-is-pro'
import { usePolar } from '@/hooks/use-polar'
import { usePromoCountdown } from '@/lib/promo'

type Reason =
  | 'goal-limit'
  | 'habit-limit'
  | 'replan'
  | 'voice'
  | 'stats'
  | 'ask-ai'
  | 'coach'
  | 'accent'
  | 'edit-goal'
  | 'calendar'
  | 'vision-video'
  | 'pro'
  | 'first-win'
  | 'trial-ended'
  | 'usage-limit'
  | 'onboarding'
  | 'lifetime'
  | 'new-year'

interface UpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reason: Reason
  title?: string
  description?: string
  // The metered feature that ran out (usage-limit only).
  feature?: UsageFeature
  // Restrict the plan cards (e.g. a Pro subscriber upgrading to lifetime only).
  onlyPlans?: PlanTerm[]
  // Bought or started a trial, as opposed to just closing the dialog.
  onUpgraded?: () => void
}

const FALLBACK_PRICING_URL = `${import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'}/pricing`

type RowKey = 'coach' | 'goals' | 'habits' | 'brainDump' | 'vision'

// The Free vs Pro row a reason/feature is about — left out of "Also in Pro"
// (it's the headline) or highlighted in "What changes".
const REASON_ROW: Partial<Record<Reason, RowKey>> = {
  voice: 'brainDump',
  coach: 'coach',
  'ask-ai': 'coach',
  'vision-video': 'vision',
  'goal-limit': 'goals',
  'habit-limit': 'habits',
}
const FEATURE_ROW: Partial<Record<UsageFeature, RowKey>> = {
  brainDump: 'brainDump',
  coachChat: 'coach',
  coachMe: 'coach',
  tts: 'coach',
  voiceCall: 'coach',
}

const REASON_ICON: Partial<Record<Reason, typeof Mic>> = {
  voice: Mic,
  'vision-video': Video,
  replan: RefreshCw,
  stats: BarChart3,
  calendar: CalendarDays,
  accent: Palette,
  'edit-goal': PencilLine,
}

function Rows({
  rows,
  heading,
  highlight,
  checksOnly,
}: {
  rows: RowKey[]
  heading: string
  highlight?: RowKey
  checksOnly?: boolean
}) {
  const { t } = useTranslation()
  const check = <Check className="size-3.5 text-primary-text" strokeWidth={3} />
  const def: Record<RowKey, { icon: ReactNode; label: string; free: string; pro: ReactNode }> = {
    coach: {
      icon: <span className="text-sm leading-none">🦁</span>,
      label: checksOnly ? t('upgrade.v.tCalls') : t('upgrade.v.rowCoach'),
      free: '—',
      pro: t('upgrade.v.rowCoachPro'),
    },
    goals: {
      icon: <Target className="size-4" />,
      label: checksOnly ? t('upgrade.v.tGoals') : t('upgrade.v.rowGoals'),
      free: String(FREE_GOAL_LIMIT),
      pro: t('upgrade.v.rowGoalsPro'),
    },
    habits: {
      icon: <Flame className="size-4" />,
      label: checksOnly ? t('upgrade.v.tHabits') : t('upgrade.v.rowHabits'),
      free: String(FREE_HABIT_LIMIT),
      pro: t('upgrade.v.rowHabitsPro'),
    },
    brainDump: {
      icon: <Mic className="size-4" />,
      label: t('upgrade.v.rowBrainDump'),
      free: '—',
      pro: check,
    },
    vision: {
      icon: <Video className="size-4" />,
      label: checksOnly ? t('upgrade.v.tVideo') : t('upgrade.v.rowVision'),
      free: t('upgrade.v.rowVisionFree'),
      pro: t('upgrade.v.rowVisionPro'),
    },
  }
  const cols = checksOnly ? 'grid-cols-[1fr_auto]' : 'grid-cols-[1fr_64px_88px]'
  return (
    <div className="overflow-hidden rounded-xl border border-border/70">
      <div
        className={cn(
          'grid bg-muted/40 px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/70',
          cols,
        )}
      >
        <span>{heading}</span>
        {!checksOnly && (
          <>
            <span>{t('upgrade.v.free')}</span>
            <span className="text-primary-text">{t('upgrade.v.pro')}</span>
          </>
        )}
      </div>
      {rows.map((key) => {
        const r = def[key]
        return (
          <div
            key={key}
            className={cn(
              'grid items-center border-t border-border/70 px-3 py-2 text-[13px]',
              cols,
              highlight === key && 'bg-primary/8',
            )}
          >
            <span
              className={cn(
                'flex min-w-0 items-center gap-2.5',
                highlight === key && 'font-semibold',
              )}
            >
              <span className="flex w-4 shrink-0 justify-center text-muted-foreground">
                {r.icon}
              </span>
              <span className="truncate">{r.label}</span>
            </span>
            {checksOnly ? (
              check
            ) : (
              <>
                <span className="text-xs text-muted-foreground/70">{r.free}</span>
                <span className="flex items-center gap-1 text-xs font-bold text-foreground">
                  {r.pro}
                </span>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Meter({
  used,
  limit,
  label,
  hint,
}: {
  used: number
  limit: number
  label: string
  hint?: string
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border/70 bg-muted/30 px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
        <b className="tabular-nums text-foreground">{label}</b>
        {hint && <span className="text-right text-muted-foreground">{hint}</span>}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-orange-500"
          style={{ width: `${limit ? Math.min(100, (used / limit) * 100) : 100}%` }}
        />
      </div>
    </div>
  )
}

const SLIDES = [
  { key: 'coach', icon: null },
  { key: 'goals', icon: Target },
  { key: 'brainDump', icon: Mic },
  { key: 'stats', icon: BarChart3 },
  { key: 'vision', icon: Video },
] as const
const SLIDE_MS = 4500

// Swipeable feature tour; auto-advances until the user swipes it themselves.
function FeatureCarousel() {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (touched) return
    const id = setInterval(() => {
      const el = ref.current
      if (!el) return
      const next = (Math.round(el.scrollLeft / el.clientWidth) + 1) % SLIDES.length
      el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' })
    }, SLIDE_MS)
    return () => clearInterval(id)
  }, [touched])

  return (
    <div>
      <div
        ref={ref}
        onPointerDown={() => setTouched(true)}
        onScroll={(e) =>
          setIndex(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))
        }
        className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {SLIDES.map(({ key, icon: Icon }) => (
          <div key={key} className="w-full shrink-0 snap-center px-5">
            <div className="flex h-full items-center gap-4 rounded-2xl border border-border/70 bg-card/60 p-4">
              <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/12 text-primary-text">
                {Icon ? <Icon className="size-6.5" /> : <CoachAvatar voice={undefined} size={44} />}
              </span>
              <div className="min-w-0">
                <p className="text-[15px] leading-snug font-extrabold text-balance">
                  {t(`upgrade.fs.slides.${key}.title`)}
                </p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">
                  {t(`upgrade.fs.slides.${key}.desc`)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex justify-center gap-1.5">
        {SLIDES.map(({ key }, i) => (
          <span
            key={key}
            className={cn(
              'h-1.5 rounded-full transition-all duration-300',
              i === index ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/25',
            )}
          />
        ))}
      </div>
    </div>
  )
}

function PlanCard({
  selected,
  onSelect,
  name,
  note,
  price,
  was,
  unit,
  badge,
  gold,
}: {
  selected: boolean
  onSelect: () => void
  name: string
  note: string
  price: string
  // Pre-sale price, struck through beside `price`.
  was?: string
  unit: string
  badge?: string
  gold?: boolean
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'relative flex w-full cursor-pointer items-center gap-3 rounded-2xl border-[1.5px] px-4 py-3.5 text-left transition-colors',
        selected ? 'border-primary bg-primary/8' : 'border-border bg-card/50 hover:bg-card',
      )}
    >
      <span
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full border-2',
          selected ? 'border-primary' : 'border-muted-foreground/40',
        )}
      >
        {selected && <span className="size-2.5 rounded-full bg-primary" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-bold">{name}</span>
        <span className="block truncate text-[11.5px] text-muted-foreground">{note}</span>
      </span>
      <span className="shrink-0 text-right">
        <span className="flex items-baseline justify-end gap-1.5">
          {was && (
            <s className="text-[12px] font-semibold text-muted-foreground tabular-nums">{was}</s>
          )}
          <b className="text-[15px] font-extrabold tabular-nums">{price}</b>
        </span>
        <span className="text-[11px] text-muted-foreground">{unit}</span>
      </span>
      {badge && (
        <span
          className={cn(
            'absolute -top-2.5 right-3 rounded-full px-2 py-0.5 text-[10px] font-extrabold tracking-wide uppercase',
            gold ? 'bg-amber-400 text-amber-950' : 'bg-primary text-primary-foreground',
          )}
        >
          {badge}
        </span>
      )}
    </button>
  )
}

function Faq({ q, a }: { q: string; a: string }) {
  return (
    <details className="group border-b border-border/70 py-3 last:border-0">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[13.5px] font-semibold [&::-webkit-details-marker]:hidden">
        {q}
        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <p className="mt-1.5 text-[12.5px] text-muted-foreground">{a}</p>
    </details>
  )
}

export function UpgradeDialog({
  open,
  onOpenChange,
  reason,
  title: titleOverride,
  description: descriptionOverride,
  feature,
  onlyPlans,
  onUpgraded,
}: UpgradeDialogProps) {
  const { t, i18n } = useTranslation()
  const { checkout, isConfigured } = usePolar()
  const { trialEligible, trial, tier } = useIsPro()
  const startTrial = useMutation(api.subscriptions.startMyProTrial)
  const [loading, setLoading] = useState(false)
  const [trialLoading, setTrialLoading] = useState(false)
  const [plan, setPlan] = useState<PlanTerm>('year')
  const posthog = usePostHog()

  const variant =
    reason === 'onboarding' && trial.active
      ? 'welcome'
      : trialEligible
        ? 'trial'
        : reason === 'trial-ended'
          ? 'ended'
          : reason === 'goal-limit'
            ? 'goals'
            : reason === 'habit-limit'
              ? 'habits'
              : reason === 'usage-limit'
                ? 'usage'
                : 'feature'

  const pricing = useQuery(api.subscriptions.getPricing, open ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, open && variant === 'goals' ? {} : 'skip')
  const habitAllowance = useQuery(
    api.nodes.getMyHabitAllowance,
    open && variant === 'habits' ? {} : 'skip',
  )
  const usage = useQuery(
    api.usage.getMyUsage,
    open && variant === 'usage' ? { tzOffsetMin: new Date().getTimezoneOffset() } : 'skip',
  )
  const recap = useQuery(api.usage.getMyTrialRecap, open && variant === 'ended' ? {} : 'skip')
  const prefs = useQuery(
    api.preferences.getMyPreferences,
    open && variant === 'ended' ? {} : 'skip',
  )

  useEffect(() => {
    if (open) posthog.capture('paywall_seen', { reason, variant })
  }, [open])

  const plans = (['year', 'month', 'lifetime'] as const).filter(
    (p) => (pricing?.available[p] ?? true) && (!onlyPlans || onlyPlans.includes(p)),
  )
  const selected: PlanTerm = plans.includes(plan) ? plan : (plans[0] ?? 'month')
  const prices = pricing?.prices ?? { month: 9, year: 72, lifetime: 149 }
  const money = (n: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n)
  const countdown = usePromoCountdown(pricing?.promo?.web ? pricing.promo : null)
  const promo = countdown.live ? pricing!.promo : null
  // The sale prices the first year; renewals are full price.
  const yearPrice = promo ? Math.round(prices.year * (100 - promo.pct)) / 100 : prices.year
  const yearMonthly = Math.round((yearPrice / 12) * 100) / 100
  const savePct = Math.round((1 - prices.year / (prices.month * 12)) * 100)

  async function handleUpgrade() {
    if (selected === 'month' && !isConfigured) {
      window.open(FALLBACK_PRICING_URL, '_blank')
      return
    }
    if (pricing && !pricing.checkout[selected]) {
      toast.error(t('upgrade.fs.planUnavailable'), {
        description: t('upgrade.fs.planUnavailableHint'),
      })
      return
    }
    setLoading(true)
    const success = await checkout(selected)
    setLoading(false)
    if (!success) return
    onUpgraded?.()
    onOpenChange(false)
  }

  async function handleStartTrial() {
    setTrialLoading(true)
    const res = await startTrial({}).catch(() => null)
    setTrialLoading(false)
    if (!res?.started) {
      toast.error(t('trial.startError'))
      return
    }
    posthog.capture('pro_trial_started', { reason })
    toast.success(t('trial.started'))
    onUpgraded?.()
    onOpenChange(false)
  }

  const spinner = (
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
  )
  const eyebrow = (text: string, className?: string) => (
    <p
      className={cn(
        'text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-primary-text',
        className,
      )}
    >
      {text}
    </p>
  )
  const iconTile = (Icon: typeof Mic, tone = 'bg-primary/10 text-primary-text') => (
    <span className={cn('flex size-12 items-center justify-center rounded-[14px]', tone)}>
      <Icon className="size-6" />
    </span>
  )
  const heading = (title: ReactNode, desc?: ReactNode) => (
    <>
      <h2 className="mt-1 text-[26px] leading-[1.1] font-black tracking-tight text-balance">
        {title}
      </h2>
      {desc && <p className="mt-2 text-[14px] text-muted-foreground">{desc}</p>}
    </>
  )

  let hero: ReactNode
  let own: RowKey | undefined
  if (variant === 'welcome') {
    hero = (
      <>
        <CoachAvatar voice={undefined} size={56} />
        <div>
          {eyebrow(t('upgrade.fs.welcomeEyebrow', { days: trial.daysLeft }))}
          {heading(t('upgrade.fs.welcomeTitle'), t('upgrade.fs.welcomeDesc'))}
        </div>
      </>
    )
  } else if (variant === 'trial') {
    hero = (
      <>
        {iconTile(Sparkles)}
        <div>
          {eyebrow(t('upgrade.v.trialEyebrow'))}
          {heading(t('upgrade.v.trialTitle'), t('upgrade.v.trialDesc'))}
        </div>
      </>
    )
  } else if (variant === 'ended') {
    const p = prefs as { coachVoice?: string; coachName?: string } | null | undefined
    const coach = coachDisplayName(p?.coachVoice, coachNameOverride(p))
    const name = (prefs as { name?: string } | null | undefined)?.name?.trim().split(/\s+/)[0]
    const stats = recap
      ? [
          { n: recap.callMinutes, label: t('upgrade.v.statCalls') },
          { n: recap.brainDumps, label: t('upgrade.v.statDumps') },
          { n: recap.videos, label: t('upgrade.v.statVideos') },
        ].filter((s) => s.n > 0)
      : []
    hero = (
      <>
        <CoachAvatar voice={p?.coachVoice} size={56} />
        <div>
          {eyebrow(t('upgrade.v.endedEyebrow'))}
          {heading(
            name
              ? t('upgrade.v.endedTitle', { coach, name })
              : t('upgrade.v.endedTitleAnon', { coach }),
            stats.length > 0 ? t('upgrade.v.endedLede') : undefined,
          )}
        </div>
        {stats.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-border/70 bg-card/60 p-2.5">
                <b className="block text-xl font-black tabular-nums">{s.n}</b>
                <span className="text-[11.5px] leading-tight text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </div>
        )}
        <p className="flex gap-2 text-[12.5px] text-muted-foreground">
          <Check className="mt-0.5 size-3.5 shrink-0 text-primary-text" strokeWidth={3} />
          {t('upgrade.v.endedKeep')}
        </p>
      </>
    )
  } else if (variant === 'goals') {
    own = 'goals'
    const active = (goals ?? []).filter(
      (g: { isInbox?: boolean; status?: string }) => !g.isInbox && g.status !== 'done',
    ) as { _id: string; title: string; category: string }[]
    const used = Math.max(active.length, FREE_GOAL_LIMIT)
    hero = (
      <>
        {iconTile(Target, 'bg-orange-500/14 text-orange-500')}
        <div>
          {eyebrow(t('upgrade.v.limitEyebrow'), 'text-orange-500')}
          {heading(t('upgrade.v.goalsTitle', { count: used }), t('upgrade.v.goalsDesc'))}
        </div>
        <Meter
          used={used}
          limit={FREE_GOAL_LIMIT}
          label={t('upgrade.v.goalsMeter', { used, limit: FREE_GOAL_LIMIT })}
          hint={t('upgrade.v.goalsHint')}
        />
        {active.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {active.slice(0, 4).map((g) => (
              <span
                key={g._id}
                className="flex max-w-full items-center gap-1.5 rounded-full border border-border/70 px-2.5 py-0.5 text-[11.5px] text-muted-foreground"
              >
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ background: `var(--${g.category?.toLowerCase()}, currentColor)` }}
                />
                <span className="truncate">{g.title}</span>
              </span>
            ))}
          </div>
        )}
      </>
    )
  } else if (variant === 'habits') {
    own = 'habits'
    const used = Math.max(habitAllowance?.used ?? 0, FREE_HABIT_LIMIT)
    hero = (
      <>
        {iconTile(Flame, 'bg-orange-500/14 text-orange-500')}
        <div>
          {eyebrow(t('upgrade.v.limitEyebrow'), 'text-orange-500')}
          {heading(t('habitLimit.title', { count: used }), t('habitLimit.desc'))}
        </div>
        <Meter
          used={used}
          limit={FREE_HABIT_LIMIT}
          label={t('habitLimit.meter', { used, limit: FREE_HABIT_LIMIT })}
          hint={t('habitLimit.hint')}
        />
      </>
    )
  } else if (variant === 'usage') {
    own = feature ? FEATURE_ROW[feature] : undefined
    const f = feature ? usage?.features?.[feature] : undefined
    const seconds = feature === 'voiceCall'
    const used = f ? (seconds ? Math.round(f.used / 60) : f.used) : null
    const limit = f ? (seconds ? Math.round(f.limit / 60) : f.limit) : null
    const now = new Date()
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    const reset =
      f?.period === 'day'
        ? t('upgrade.v.resetsTomorrow')
        : f?.period === 'month'
          ? t('upgrade.v.resetsMonth', {
              date: nextMonth.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
            })
          : t('upgrade.v.trialAllowance')
    hero = (
      <>
        {iconTile(Gauge, 'bg-orange-500/14 text-orange-500')}
        <div>
          {eyebrow(t('upgrade.v.limitEyebrow'), 'text-orange-500')}
          {heading(
            titleOverride ?? t('upgrade.usage-limit.title'),
            descriptionOverride ?? t('upgrade.usage-limit.desc'),
          )}
        </div>
        {used !== null && limit !== null && (
          <Meter
            used={used}
            limit={limit}
            label={t('upgrade.v.usageMeter', { used, limit })}
            hint={reset}
          />
        )}
      </>
    )
  } else {
    const isVoice = reason === 'voice'
    const isCoach = reason === 'coach' || reason === 'ask-ai'
    own = REASON_ROW[reason]
    const Icon = REASON_ICON[reason] ?? Sparkles
    hero = (
      <>
        {isCoach ? <CoachAvatar voice={undefined} size={56} /> : iconTile(Icon)}
        <div>
          {eyebrow(
            reason === 'pro' || reason === 'first-win' ? 'Goaltree Pro' : t('upgrade.v.eyebrowPro'),
          )}
          {heading(
            titleOverride ?? (isVoice ? t('upgrade.v.voiceTitle') : t(`upgrade.${reason}.title`)),
            descriptionOverride ??
              (isVoice ? t('upgrade.v.voiceDesc') : t(`upgrade.${reason}.desc`)),
          )}
        </div>
        {isVoice && (
          <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-linear-to-b from-primary/10 to-transparent p-3.5">
            <div className="flex h-6 items-center gap-[3px]" aria-hidden>
              {[0, 0.12, 0.24, 0.08, 0.3, 0.18].map((d) => (
                <span
                  key={d}
                  className="w-[3px] animate-[upgrade-wave_1.1s_ease-in-out_infinite] rounded-full bg-primary"
                  style={{ animationDelay: `${d}s`, height: 4 }}
                />
              ))}
            </div>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground/60" />
            <div className="flex min-w-0 flex-col gap-1">
              {(['health', 'finance', 'relationships'] as const).map((c, i) => (
                <span
                  key={c}
                  className="flex items-center gap-1.5 truncate rounded-lg border border-border/70 bg-muted/50 px-2 py-0.5 text-[11.5px]"
                >
                  <span
                    className="size-1.5 shrink-0 rounded-full"
                    style={{ background: `var(--${c})` }}
                  />
                  <span className="truncate">{t(`upgrade.v.demo${i + 1}`)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </>
    )
  }

  const planCard: Record<PlanTerm, ReactNode> = {
    year: (
      <PlanCard
        key="year"
        selected={selected === 'year'}
        onSelect={() => setPlan('year')}
        name={t('upgrade.fs.planYear')}
        note={
          promo
            ? t('upgrade.fs.planYearPromoNote', {
                price: money(yearPrice),
                full: money(prices.year),
              })
            : t('upgrade.fs.planYearNote', { price: money(prices.year) })
        }
        price={money(yearMonthly)}
        was={promo ? money(Math.round((prices.year / 12) * 100) / 100) : undefined}
        unit={t('upgrade.fs.perMonth')}
        badge={
          promo ? t('promo.badge', { pct: promo.pct }) : t('upgrade.fs.save', { pct: savePct })
        }
      />
    ),
    month: (
      <PlanCard
        key="month"
        selected={selected === 'month'}
        onSelect={() => setPlan('month')}
        name={t('upgrade.fs.planMonth')}
        note={t('upgrade.fs.planMonthNote')}
        price={money(prices.month)}
        unit={t('upgrade.fs.perMonth')}
      />
    ),
    lifetime: (
      <PlanCard
        key="lifetime"
        selected={selected === 'lifetime'}
        onSelect={() => setPlan('lifetime')}
        name={t('upgrade.fs.planLifetime')}
        note={t('upgrade.fs.planLifetimeNote', {
          left: pricing?.lifetimeSeatsLeft ?? 0,
          total: pricing?.lifetimeSeats ?? 0,
        })}
        price={money(prices.lifetime)}
        unit={t('upgrade.fs.once')}
        badge={t('upgrade.fs.limited')}
        gold
      />
    ),
  }

  const ctaLabel = {
    year: promo
      ? t('upgrade.fs.ctaYearPromo', { price: money(yearPrice) })
      : t('upgrade.fs.ctaYear', { price: money(yearPrice) }),
    month: t('upgrade.fs.ctaMonth', { price: money(prices.month) }),
    lifetime: t('upgrade.fs.ctaLifetime', { price: money(prices.lifetime) }),
  }[selected]
  const fineLabel = {
    year: promo
      ? t('upgrade.fs.fineYearPromo', { price: money(yearPrice), full: money(prices.year) })
      : t('upgrade.fs.fineYear'),
    month: t('upgrade.fs.fineMonth'),
    lifetime: t('upgrade.fs.fineLifetime'),
  }[selected]
  const showTrial = variant === 'trial'
  const closeLabel =
    variant === 'welcome'
      ? t('upgrade.fs.keepTrial')
      : variant === 'ended'
        ? t('upgrade.v.stayFree')
        : tier === 'free'
          ? t('upgrade.fs.continueFree')
          : t('upgrade.v.notNow')
  const primary =
    'inline-flex h-13 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15.5px] font-extrabold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70'
  const sectionTitle = (text: string) => (
    <p className="px-5 text-[11px] font-extrabold tracking-[0.14em] text-muted-foreground uppercase">
      {text}
    </p>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="inset-0 top-0 left-0 z-[80] flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none duration-300 ease-[cubic-bezier(.22,1,.36,1)] data-ending-style:translate-y-full data-ending-style:scale-100 data-ending-style:opacity-100 data-starting-style:translate-y-full data-starting-style:scale-100 data-starting-style:opacity-100 sm:inset-auto sm:top-[4dvh] sm:left-1/2 sm:h-[92dvh] sm:max-h-[900px] sm:max-w-md sm:-translate-x-1/2 sm:rounded-3xl sm:border sm:shadow-2xl"
      >
        <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-primary/22 via-primary/6 to-transparent" />
          <div className="sticky top-0 z-10 flex justify-between px-3 pt-[calc(env(safe-area-inset-top,0px)+10px)]">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={t('common.close')}
              className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-background/60 text-muted-foreground outline-none backdrop-blur transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="relative flex flex-col gap-4 px-5 pt-2">{hero}</div>

          <div className="relative mt-7 flex flex-col gap-3">
            {sectionTitle(t('upgrade.fs.whatYouGet'))}
            <FeatureCarousel />
          </div>

          <div className="mt-7 flex flex-col gap-3">
            {sectionTitle(t('upgrade.fs.choosePlan'))}
            {promo && (
              <div className="mx-5 flex flex-col items-center gap-0.5 rounded-xl bg-primary/10 px-4 py-2.5 text-center">
                <p className="text-sm font-bold text-primary-text">
                  {t('promo.strip', { pct: promo.pct })}
                </p>
                <p className="text-xs font-semibold tabular-nums text-foreground/70">
                  {countdown.lastDay && (
                    <span className="mr-1.5 rounded-full bg-primary px-1.5 py-px text-[10px] font-extrabold uppercase text-primary-foreground">
                      {t('promo.lastDay')}
                    </span>
                  )}
                  {countdown.label}
                </p>
              </div>
            )}
            <div role="radiogroup" className="flex flex-col gap-3 px-5">
              {plans.map((p) => planCard[p])}
            </div>
          </div>

          <div className="mt-7 flex flex-col gap-3 px-5">
            <Rows
              rows={['goals', 'habits', 'coach', 'brainDump', 'vision']}
              heading={t('upgrade.fs.compare')}
              highlight={own}
            />
            <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-muted-foreground">
              {(
                [
                  [RefreshCw, t('upgrade.fs.trustCancel')],
                  [Lock, t('upgrade.fs.trustSecure')],
                  [ShieldCheck, t('upgrade.fs.trustKeep')],
                ] as const
              ).map(([Icon, label]) => (
                <span
                  key={label}
                  className="flex flex-col items-center gap-1.5 rounded-xl bg-muted/30 px-2 py-2.5"
                >
                  <Icon className="size-4 text-primary-text" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-7 px-5 pb-8">
            <p className="mb-1 text-[11px] font-extrabold tracking-[0.14em] text-muted-foreground uppercase">
              {t('upgrade.fs.faqTitle')}
            </p>
            {(['cancel', 'keep', 'lifetime', 'trial'] as const)
              .filter((k) => k !== 'lifetime' || plans.includes('lifetime'))
              .map((k) => (
                <Faq key={k} q={t(`upgrade.fs.faq.${k}Q`)} a={t(`upgrade.fs.faq.${k}A`)} />
              ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-border/70 bg-background/95 px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] backdrop-blur">
          {showTrial ? (
            <>
              <button
                type="button"
                onClick={handleStartTrial}
                disabled={trialLoading}
                className={primary}
              >
                {trialLoading ? spinner : t('upgrade.v.startTrial')}
              </button>
              <button
                type="button"
                onClick={handleUpgrade}
                disabled={loading}
                className="mt-1 w-full cursor-pointer py-2 text-center text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                {loading ? t('upgrade.ctaLoading') : t('upgrade.fs.orPayNow', { plan: ctaLabel })}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={handleUpgrade} disabled={loading} className={primary}>
                {loading ? (
                  <>
                    {spinner}
                    {t('upgrade.ctaLoading')}
                  </>
                ) : (
                  ctaLabel
                )}
              </button>
              <p className="mt-2 text-center text-[11.5px] text-muted-foreground/80">{fineLabel}</p>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full cursor-pointer pt-1.5 text-center text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                {closeLabel}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
